import React, { memo, useState, useEffect } from "react"
import { graphql } from "gatsby"
import * as styles from "../page_css/page.css"
import SEOHead from "../components/head"
import { TFood } from "../types"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Layout from "../components/layout"
import { capitalize } from "../utils"
import StandardCard from "../components/blocks/StandardCard"
import allReusableCards from "../staticQueries/allReusableCards"
import IconTikTok from "../graphics/icon-tiktok.svg"
import IconInstagram from "../graphics/icon-instagram.svg"
import IconFacebook from "../graphics/icon-facebook.svg"
import IconTwitter from "../graphics/icon-twitter.svg"
import IconYoutube from "../graphics/icon-youtube.svg"
import IconOpentable from "../graphics/icon-opentable.svg"
import IconLink from "../graphics/icon-link.svg"
import IconFood from "../graphics/icon-food.svg"
import IconLocation from "../graphics/icon-location.svg"
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import * as blocks from "../components/blocks"
import Fallback from "../components/fallback"


import {
  breakpoints
} from "../components/ui.css"

export interface FoodProps {
  data: {
    food: TFood
  }
}

const getWindowWidth = () => window.innerWidth 
  || document.documentElement.clientWidth 
  || document.body.clientWidth

const desktopBool = () => {
  if (typeof window !== "undefined") {
    return getWindowWidth() > breakpoints[2] ? true : false
  }
}

export default function Food(props: FoodProps) {
  const [isDesktop, setIsDesktop] = useState(desktopBool())
  const [hasWindow, setHasWindow] = useState(false)

  const reusableCards = allReusableCards()
  const ticketCard = reusableCards.filter(card => card.title.includes("Ticket"))[0].cardData[0]

  const food = props.data.food
  const unformattedFeatures = food.features ? eval(food.features.replace('\\n  \\', '')) as [] : []
  const attributes = unformattedFeatures.map(feature => {
    return capitalize(feature)
  })
  const cuisines = food.cuisines.map(cuisine => {
    return cuisine.title
  })

  const features = cuisines.concat(attributes).join(', ')

  //svg icon size
  const size = {height:"24px", width:"24px"};
  const allContent = props.data.food.content?.map((block) => {
    const { id, blocktype, desktopRight, ...componentProps } = block
    const Component = memo(blocks[blocktype] || Fallback)
    const column = desktopRight ? "right" : "left"
    return { 
      column: column,
      component: <Component key={id} id={id} blocktype={blocktype} {...(componentProps as any)} />,
    }
  })

  const leftContent = allContent?.filter(block => block.column == "left")
  const rightContent = allContent?.filter(block => block.column == "right")
   
  useEffect(() => {
    const handleIsDesktop = () => {
      setIsDesktop(desktopBool())
    }
    if (typeof window !== "undefined") {
      setHasWindow(true)
      window.addEventListener('resize', handleIsDesktop);
      return () => {
        window.removeEventListener('resize', handleIsDesktop);
      }
    }
  }, [])

  const markdownContent = props.data?.food?.content;

  return (
    <Layout location="food-page">
      <div className={styles.pageContainer}>
      {hasWindow && (
          <>
            <section className={`${styles.pageColumn} music`}>
            <>
              {food.largeProfileImage ? (
                <GatsbyImage
                  alt={food.title}
                  className={styles.foodImage}
                  image={getImage(food.largeProfileImage)}
                />
              ) : (
                <GatsbyImage
                  className={styles.foodImage}
                  alt={food.title}
                  image={getImage(food.avatar)}
                />
              )}
            </>
          <div className={styles.foodMeta}>
            <h1 className={styles.pageHeroTitle} style={{ paddingLeft: 0, marginTop: 0 }}>{food.title}</h1>
            {(food.city) && (
              <div className={styles.contributorMeta}>
                {food.city && (
                  <strong>
                    {food.city}
                    {food.state && (
                      <>, {food.state}</>
                    )}
                  </strong>
                )}
              </div>
            )}
            {features && (
              <div className={styles.contributorMeta}>
                <IconFood style={{size}} /> <div style={{ flex: 1 }}>{features}</div>
              </div>
            )}
            {food.locations?.length > 0 && (
              <div className={styles.contributorMeta}>
                <IconLocation /> {food.locations?.map(location => location.title)}
              </div>
            )}
            <div className={styles.contributorSocial}>
              {food.website && (<a href={food.website} target="_blank"><IconLink style={size}/></a> )}
              {food.facebook && (<a href={food.facebook} target="_blank"><IconFacebook style={size}/></a> )}
              {food.instagram && (<a href={food.instagram} target="_blank"><IconInstagram style={size}/></a> )}
              {food.opentable && (<a href={food.opentable} target="_blank"><IconOpentable style={size}/></a> )}
              {food.tiktok && (<a href={food.tiktok} target="_blank"><IconTikTok style={size}/></a> )}
              {food.twitter && (<a href={food.twitter} target="_blank"><IconTwitter style={size}/></a> )}
              {food.youtube && (<a href={food.youtube} target="_blank"><IconYoutube style={size}/></a> )}
            </div>
            <div className={styles.foodSummary}>
              {!props.data.food.content.length && (
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                    >{props.data.food.summary}</ReactMarkdown>
                )}
                {!isDesktop ? 
                allContent?.map((block) => block.component ) : 
                leftContent?.map((block) => block.component )
              }
            </div>


          </div>
          </section>
            {isDesktop && (
              <section className={styles.pageColumn}>
                
                <StandardCard {...ticketCard} />
                
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  )
}
export const Head = (props: FoodProps ) => {
  const food = props.data.food
  const seoTitle = food.metadata?.title || food.title
  const seoDescription = food.metadata?.description || food.summary
  const seoImage = food.largeProfileImage ? food.largeProfileImage.localFile: food.avatar.localFile
  return <SEOHead title={seoTitle} image={seoImage} description={seoDescription} />
}

export const query = graphql`
  query Food($id: String!) {
    food(id: { eq: $id }) {
      ...FoodContent
      content {
        ...BlockContent
      }
    }
  }
`